import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { Brain, BarChart2, Info, AlertCircle, RefreshCw, Download, FileText } from 'lucide-react';

// Color palette for visualization
const COMPARTMENT_COLORS = {
  'Presynaptic': '#2563eb',
  'Postsynaptic': '#16a34a',
  'Synaptic_Vesicle': '#9333ea',
  'Synaptosome': '#d97706',
};

const hexToRgb = (hex) => {
  // Remove the hash if it exists
  hex = hex.replace(/^#/, '');

  // Parse the hex values
  let r, g, b;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    return [0, 0, 0]; // Default if invalid hex
  }

  return [r, g, b];
};

// Custom hook for fetching data
const useBrainRegionData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Brain Region Protein Distribution'; // Set the page title
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/proteins/brain-regions');
        if (!response.ok) {
          throw new Error(`API returned status: ${response.status}`);
        }
        const result = await response.json();
        if (result.success) {
          setData(result);
        } else {
          throw new Error(result.message || 'Failed to fetch data');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading brain region data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
};

// CSV Download Button Component with enhanced error handling
const DownloadButton = ({ url, filename, label, color = "blue", icon = <Download size={14} className="mr-1" />, className = "" }) => {
  const [downloadStatus, setDownloadStatus] = useState(null);

  const handleDownload = async () => {
    try {
      setDownloadStatus('downloading');

      // Try the fetch API approach first
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      // Check content type to ensure we got a CSV
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // We got JSON (probably an error) instead of CSV
        const errorData = await response.json();
        console.error('Error downloading CSV:', errorData);
        setDownloadStatus('error');
        setTimeout(() => setDownloadStatus(null), 3000);
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create object URL
      const objectUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger the download
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename || 'download.csv');
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setDownloadStatus('success');
      setTimeout(() => setDownloadStatus(null), 2000);

    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      setTimeout(() => setDownloadStatus(null), 3000);

      // Fallback to direct link approach
      try {
        console.log('Trying fallback download method for:', url);
        window.open(url, '_blank');
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);
      }
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloadStatus === 'downloading'}
      className={`px-2 py-1 text-xs flex items-center rounded border hover:bg-${color}-50 text-${color}-700 border-${color}-300 ${className} ${downloadStatus === 'downloading' ? 'opacity-50 cursor-wait' : ''
        } ${downloadStatus === 'error' ? 'bg-red-50 text-red-700 border-red-300' : ''} 
      ${downloadStatus === 'success' ? 'bg-green-50 text-green-700 border-green-300' : ''}`}
    >
      {downloadStatus === 'downloading' ? (
        <RefreshCw size={14} className="mr-1 animate-spin" />
      ) : downloadStatus === 'error' ? (
        <AlertCircle size={14} className="mr-1" />
      ) : downloadStatus === 'success' ? (
        <div className="mr-1">✓</div>
      ) : (
        icon
      )}
      {downloadStatus === 'downloading' ? 'Downloading...' :
        downloadStatus === 'error' ? 'Failed' :
          downloadStatus === 'success' ? 'Success!' : label}
    </button>
  );
};

// Formats brain region names for better display
const formatRegionName = (name) => {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Formats compartment names for better display
const formatCompartmentName = (name) => {
  if (!name) return '';
  const formatted = name.replace(/_/g, ' ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

// Heat Map component for protein distribution visualization
const ProteinDistributionHeatMap = ({
  data,
  sortedRegions,
  selectedRegion,
  onSelectRegion,
  selectedCompartment,
  onSelectCompartment
}) => {
  const maxProteinCount = Math.max(
    ...data.data.flatMap(region =>
      region.compartments.map(comp => comp.protein_count)
    ), 1
  );

  const getColorIntensity = (count) => {
    const intensity = Math.min(0.1 + (count / maxProteinCount) * 0.9, 1);
    return intensity;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-200">
        <thead>
          <tr>
            <th className="p-2 border border-gray-200 text-left bg-gray-50 sticky left-0 z-10">Brain Region</th>
            {data.compartments.map(comp => (
              <th
                key={comp}
                className={`p-2 border border-gray-200 text-center cursor-pointer
                  ${selectedCompartment === comp ? 'bg-blue-50' : 'bg-gray-50'}`}
                onClick={() => onSelectCompartment(comp)}
              >
                <div className="flex items-center justify-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: COMPARTMENT_COLORS[comp] }}
                  />
                  <span>{formatCompartmentName(comp)}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRegions.map(region => {
            const isSelected = selectedRegion && selectedRegion.region_id === region.region_id;
            const regionCompartments = {};

            // Map compartments for easy lookup
            region.compartments.forEach(comp => {
              regionCompartments[comp.name] = comp;
            });

            return (
              <tr
                key={region.region_id}
                className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
              >
                <td
                  className="p-2 border border-gray-200 sticky left-0 bg-white cursor-pointer z-10"
                  onClick={() => onSelectRegion(region.region_id)}
                >
                  <div className="flex items-center">
                    <span className="font-medium">{formatRegionName(region.name)}</span>
                    {region.description && (
                      <div className="ml-2 text-gray-400 cursor-help relative group">
                        <Info size={16} />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 z-20">
                          {region.description}
                        </div>
                      </div>
                    )}
                  </div>
                </td>

                {data.compartments.map(comp => {
                  const compartmentData = regionCompartments[comp] || { protein_count: 0 };
                  const count = compartmentData.protein_count;
                  const opacity = getColorIntensity(count);

                  return (
                    <td
                      key={`${region.region_id}-${comp}`}
                      className="p-2 border border-gray-200 text-center cursor-pointer"
                      style={{
                        backgroundColor: count > 0
                          ? `rgba(${hexToRgb(COMPARTMENT_COLORS[comp]).join(', ')}, ${opacity})`
                          : 'transparent'
                      }}
                      onClick={() => count > 0 && onSelectRegion(region.region_id, comp)}
                    >
                      {count > 0 ? count : '-'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Region Detail component
const RegionDetail = ({ region, selectedCompartment, onSelectCompartment, apiData }) => {
  const compartmentData = selectedCompartment
    ? region.compartments.find(c => c.name === selectedCompartment)
    : null;

  // Generate chart data for selected region's compartments
  const compartmentChartData = region.compartments.map(comp => ({
    name: formatCompartmentName(comp.name),
    proteins: comp.protein_count,
    papers: comp.paper_count,
    compartment: comp.name
  }));

  // Generate chart data for top proteins if compartment is selected
  const topProteinsChartData = compartmentData?.top_proteins?.map(protein => ({
    name: protein.name,
    papers: protein.paper_count
  })) || [];

  // Generate chart data for species distribution if compartment is selected
  const speciesDistributionData = compartmentData?.species_distribution?.map(species => ({
    name: species.species_name,
    count: species.protein_count
  })) || [];

  // Calculate the total proteins for the region
  // For whole brain (ID=1), use the global total_proteins_mapped value
  const totalRegionProteins = region.region_id === 1 && apiData
    ? apiData.total_proteins_mapped // For whole brain, use the accurate distinct count
    : region.distinct_protein_count || region.compartments.reduce((sum, comp) => sum + comp.protein_count, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">
          {formatRegionName(region.name)}
        </h3>

        {/* Download all region data button */}
        <DownloadButton
          url={`/api/proteins/brain-regions/${region.region_id}/All/csv`}
          filename={`${region.name.toLowerCase().replace(/ /g, '_')}_all_proteins.csv`}
          label="Download All"
          icon={<FileText size={14} className="mr-1" />}
        />
      </div>

      <div className="p-4">
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2 text-gray-700 flex items-center">
            <BarChart2 size={16} className="mr-1" />
            Compartment Distribution
          </h4>
          <div className="bg-gray-50 p-2 rounded border border-gray-200">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={compartmentChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip
                  formatter={(value, name) => [value, name === 'proteins' ? 'Proteins' : 'Papers']}
                  labelFormatter={(value) => `${value}`}
                />
                <Legend />
                <Bar
                  dataKey="proteins"
                  name="Proteins"
                  radius={[4, 4, 0, 0]}
                >
                  {compartmentChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COMPARTMENT_COLORS[entry.compartment]}
                      cursor="pointer"
                      onClick={() => onSelectCompartment(entry.compartment)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2 text-gray-700 flex items-center">
            <Info size={16} className="mr-1" />
            Region Details
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-gray-50 p-2 rounded border border-gray-200">
              <span className="text-gray-500">Name:</span> {region.name}
            </div>
            {region.interlex_id && (
              <div className="bg-gray-50 p-2 rounded border border-gray-200">
                <span className="text-gray-500">Interlex:</span> {region.interlex_id}
              </div>
            )}
            <div className="bg-gray-50 p-2 rounded border border-gray-200">
              <span className="text-gray-500">Unique Proteins:</span> {totalRegionProteins}
            </div>
            <div className="bg-gray-50 p-2 rounded border border-gray-200">
              <span className="text-gray-500">Total Papers:</span> {region.compartments.reduce((sum, comp) => sum + comp.paper_count, 0)}
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium mb-2 text-gray-700">Select a Compartment</h4>
          <div className="flex flex-wrap gap-2">
            {region.compartments.map(comp => (
              <button
                key={comp.name}
                className={`px-2 py-1 text-xs rounded-full cursor-pointer border 
                  ${selectedCompartment === comp.name
                    ? 'text-white'
                    : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                style={{
                  backgroundColor: selectedCompartment === comp.name ? COMPARTMENT_COLORS[comp.name] : 'transparent',
                  borderColor: COMPARTMENT_COLORS[comp.name]
                }}
                onClick={() => onSelectCompartment(comp.name)}
              >
                {formatCompartmentName(comp.name)}: {comp.protein_count} proteins
              </button>
            ))}
          </div>
        </div>

        {selectedCompartment && compartmentData && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Info size={16} className="mr-1" />
                {formatCompartmentName(selectedCompartment)} Details
              </h4>

              {/* Download compartment-specific data */}
              <DownloadButton
                url={`/api/proteins/brain-regions/${region.region_id}/${selectedCompartment}/csv`}
                filename={`${region.name.toLowerCase().replace(/ /g, '_')}_${selectedCompartment.toLowerCase()}_proteins.csv`}
                label="Download Data"
                color={COMPARTMENT_COLORS[selectedCompartment].replace('#', '')}
                icon={<Download size={14} className="mr-1" />}
                className="bg-white"
              />
            </div>

            {/* Top proteins chart */}
            {topProteinsChartData.length > 0 && (
              <div className="mb-4">
                <h5 className="text-xs font-medium mb-1 text-gray-600">Top Proteins</h5>
                <div className="bg-gray-50 p-2 rounded border border-gray-200">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={topProteinsChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value) => [`${value} papers`, 'Papers']}
                      />
                      <Bar
                        dataKey="papers"
                        name="Papers"
                        fill={COMPARTMENT_COLORS[selectedCompartment]}
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Species distribution chart */}
            {speciesDistributionData.length > 0 && (
              <div className="mb-4">
                <h5 className="text-xs font-medium mb-1 text-gray-600">Species Distribution</h5>
                <div className="bg-gray-50 p-2 rounded border border-gray-200">
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={speciesDistributionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value) => [`${value} proteins`, 'Proteins']}
                      />
                      <Bar
                        dataKey="count"
                        name="Proteins"
                        fill={COMPARTMENT_COLORS[selectedCompartment]}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-50 p-2 rounded border border-gray-200">
                <span className="text-gray-500">Proteins:</span> {compartmentData.protein_count}
              </div>
              <div className="bg-gray-50 p-2 rounded border border-gray-200">
                <span className="text-gray-500">Papers:</span> {compartmentData.paper_count}
              </div>
              <div className="bg-gray-50 p-2 rounded border border-gray-200">
                <span className="text-gray-500">Species:</span>{' '}
                {compartmentData.species_distribution?.length > 0
                  ? compartmentData.species_distribution.map(species => species.species_name).join(', ')
                  : 'None'
                }
              </div>
            </div>

            {compartmentData.top_proteins && compartmentData.top_proteins.length > 0 && (
              <div className="mt-4">
                <h5 className="text-xs font-medium mb-1 text-gray-600">All Proteins</h5>
                <div className="max-h-40 overflow-y-auto bg-gray-50 p-2 rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 tracking-wider">Protein</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 tracking-wider">Papers</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {compartmentData.top_proteins.map(protein => (
                        <tr key={protein.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1 text-xs text-gray-900">{protein.name}</td>
                          <td className="px-2 py-1 text-xs text-gray-900 text-right">{protein.paper_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const BrainRegionProteinDistribution = () => {
  const { data, loading, error } = useBrainRegionData();
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCompartment, setSelectedCompartment] = useState(null);

  // Prepare data when loaded
  const sortedRegions = React.useMemo(() => {
    if (!data) return [];

    // Sort by name by default
    let regions = [...data.data];
    regions.sort((a, b) => a.name.localeCompare(b.name));

    return regions;
  }, [data]);

  // Create a map for brain region hierarchy
  const regionMap = React.useMemo(() => {
    const map = new Map();

    if (data) {
      data.data.forEach(region => {
        map.set(region.region_id, {
          ...region
        });
      });
    }

    return map;
  }, [data]);

  // Auto-select the first region when data is loaded
  useEffect(() => {
    if (sortedRegions.length > 0 && !selectedRegion) {
      // Select the first region in the sorted list
      const firstRegion = sortedRegions[0];
      if (regionMap.has(firstRegion.region_id)) {
        setSelectedRegion(regionMap.get(firstRegion.region_id));
      }
    }
  }, [sortedRegions, selectedRegion, regionMap]);

  // Handle region selection
  const handleSelectRegion = (regionId, compartmentName = null) => {
    if (regionMap.has(regionId)) {
      setSelectedRegion(regionMap.get(regionId));
      if (compartmentName) {
        setSelectedCompartment(compartmentName);
      }
    }
  };

  // Handle compartment selection
  const handleSelectCompartment = (compartment) => {
    setSelectedCompartment(compartment === selectedCompartment ? null : compartment);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center">
              <Brain size={24} className="mr-2 text-blue-600" />
              Brain Region Protein Distribution
            </h2>
            <p className="mt-2 text-gray-600">
              Loading protein distribution data...
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-12">
              <div className="bg-white p-4 rounded-md border border-gray-200">
                <div className="mb-4 flex justify-between items-center">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                  </div>
                </div>

                <div className="animate-pulse">
                  <div className="h-[500px] bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center">
              <AlertCircle size={24} className="mr-2" />
              Error Loading Data
            </h2>
            <p className="mt-2 text-gray-600">
              We encountered an error while loading the brain region protein distribution data.
            </p>
          </div>
        </div>
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{error}</p>
            <button
              className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded border border-red-300 flex items-center"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={16} className="mr-2" />
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Brain size={24} className="mr-2 text-blue-600" />
              Brain Region Protein Distribution
            </h1>
            <p className="mt-2 text-gray-600">
              Distribution of {data.consensus_gene_count} consensus proteins across brain regions and compartments.
            </p>
          </div>

          {/* Global Download Button */}
          <DownloadButton
            url="/api/proteins/brain-regions/csv"
            filename="all_brain_regions_proteins.csv"
            label="Download Full Dataset"
            className="bg-white text-blue-700 border-blue-300 px-3 py-2 text-sm"
            icon={<FileText size={16} className="mr-2" />}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className={`${selectedRegion ? 'lg:col-span-8' : 'lg:col-span-12'}`}>
            <div className="bg-white p-4 rounded-md border border-gray-200">
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-medium flex items-center">
                  <BarChart2 size={20} className="mr-2 text-blue-500" />
                  Protein Heatmap
                </h3>
                <div className="flex items-center text-sm text-gray-500">
                  {data.data.length} regions • {data.compartments.length} compartments
                </div>
              </div>

              <div className="mb-4">
                <div className="flex flex-wrap gap-2 items-center text-sm">
                  <span className="text-gray-700">Download by compartment:</span>
                  {data.compartments.map(comp => (
                    <DownloadButton
                      key={comp}
                      url={selectedRegion ?
                        `/api/proteins/brain-regions/${selectedRegion.region_id}/${comp}/csv` :
                        `/api/proteins/compartment/${comp}/csv`}
                      filename={selectedRegion ?
                        `${selectedRegion.name.toLowerCase().replace(/ /g, '_')}_${comp.toLowerCase()}_proteins.csv` :
                        `${comp.toLowerCase()}_proteins.csv`}
                      label={formatCompartmentName(comp)}
                      color="gray"
                      className="bg-white"
                      icon={
                        <div
                          className="w-2 h-2 rounded-full mr-1"
                          style={{ backgroundColor: COMPARTMENT_COLORS[comp] }}
                        />
                      }
                    />
                  ))}
                </div>
              </div>

              <ProteinDistributionHeatMap
                data={data}
                sortedRegions={sortedRegions}
                selectedRegion={selectedRegion}
                onSelectRegion={handleSelectRegion}
                selectedCompartment={selectedCompartment}
                onSelectCompartment={handleSelectCompartment}
              />
            </div>
          </div>

          {selectedRegion && (
            <div className="lg:col-span-4">
              <RegionDetail
                region={selectedRegion}
                selectedCompartment={selectedCompartment}
                onSelectCompartment={handleSelectCompartment}
                apiData={data}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrainRegionProteinDistribution;